import React from 'react';
import { useForm } from 'react-hook-form';
import { useRegistration } from '../Registration';
import { useLanguage } from '../../../contexts/LanguageContext';

const Step1AccountInfo = () => {
  const { formData, updateFormData, nextStep } = useRegistration();
  const { t } = useLanguage();
  const copy = t('registration.steps.account', {});
  const requirements = Array.isArray(copy.requirements) ? copy.requirements : [];
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword
    }
  });

  const password = watch('password');

  const onSubmit = (data) => {
    updateFormData(data);
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {copy.title}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {copy.description}
        </p>
      </div>

      <div className="space-y-6">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.fields?.email?.label}
          </label>
          <input
            {...register('email', { 
              required: t('validation.emailRequired'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('validation.emailInvalid')
              }
            })}
            type="email"
            id="email"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            placeholder={copy.fields?.email?.placeholder}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.fields?.password?.label}
          </label>
          <input
            {...register('password', { 
              required: t('validation.passwordRequired'),
              minLength: { value: 6, message: t('validation.passwordMin') }
            })}
            type="password"
            id="password"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            placeholder={copy.fields?.password?.placeholder}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.fields?.confirmPassword?.label}
          </label>
          <input
            {...register('confirmPassword', { 
              required: t('validation.passwordConfirmRequired'),
              validate: value => value === password || t('validation.passwordMismatch')
            })}
            type="password"
            id="confirmPassword"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            placeholder={copy.fields?.confirmPassword?.placeholder}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Password Requirements */}
        <div className="bg-prayer-red/5 border border-prayer-red/20 rounded-lg p-4">
          <h3 className="text-sm font-medium text-prayer-red mb-2">{copy.requirementsTitle}</h3>
          <ul className="text-sm text-prayer-red/80 space-y-1">
            {requirements.map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-prayer-red text-white py-3 px-6 rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200 focus:ring-2 focus:ring-prayer-red focus:ring-offset-2"
      >
        {t('registration.next')}
      </button>
    </form>
  );
};

export default Step1AccountInfo;
